import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultPeriodReportFilters } from '../../../constants/reprots';
import { reportsStore } from '../../../store/reportsStore';
import { IReportRow } from '../../../types/reports';
import { IDailyReportParams, IHourlyReportParams, IMonthlyReportParams, IPeriodReportFilters, IPeriodReports } from '../types/salesPeriod';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import wms from 'src/services/wms';
import { downloadFile, getCancelToken } from 'src/utils/helpers';
import { subtractDatePeriod } from 'src/view/screens/ReportsScreen/helpers/reports';
import { TIME_FORMAT, dateFormatForRequest } from '~/constants/dateTime';
import { allOptionsValue } from '~/constants/main';
import logger from '~/services/logger';
import { IResponseReportBody } from '~/types/reports';
import { toastSuccess } from '~/utils/toasts';

export class SalesPeriodStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancelReportBuild: {
    [key: string]: null | Canceler;
  } = {
    hourly: null,
    daily: null,
    monthly: null,
  };

  @observable filters: IPeriodReportFilters = defaultPeriodReportFilters;
  @observable reports: IPeriodReports = {
    hourly: {
      current_period: undefined,
      previous_period: undefined,
      loading: false,
    },
    daily: {
      current_period: undefined,
      previous_period: undefined,
      loading: false,
    },
    monthly: {
      current_period: undefined,
      previous_period: undefined,
      loading: false,
    },
  };

  @action setFilters = (filters: Partial<IPeriodReportFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action fetchHourlyReport = async (isCurrentPeriod: boolean) => {
    try {
      const params: IHourlyReportParams = {
        ...reportsStore.getReportBuildParams(),
        hours: this.filters.hours.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.hours.map((option) => option.value as string),
      };

      if (!isCurrentPeriod) {
        [params.from, params.to] = subtractDatePeriod(params.from, params.to, this.filters.comparedPeriod);
      }

      if (isCurrentPeriod) this.reports.hourly.loading = true;
      let hourlyReport = await client.get('v3/reports/sales/hourly', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.hourly = c)),
      });
      hourlyReport = this.formatReport(hourlyReport, 'hour');

      if (isCurrentPeriod) {
        this.reports.hourly = {
          current_period: hourlyReport,
          previous_period: undefined,
        };
        reportsStore.selectAllBranchesInFirstInit();
        this.reports.hourly.loading = false;
      } else {
        this.reports.hourly.previous_period = hourlyReport;
      }
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports.hourly.loading = false;
      logger.error({ error, place: 'SalesPeriodStore fetchHourlyReport' });
    }
  };

  @action fetchDailyReport = async (isCurrentPeriod: boolean) => {
    try {
      const params: IDailyReportParams = {
        ...reportsStore.getReportBuildParams(),
        days: this.filters.days.some((option) => option.value === allOptionsValue) ? [] : this.filters.days.map((option) => option.value as number),
      };

      if (!isCurrentPeriod) {
        [params.from, params.to] = subtractDatePeriod(params.from, params.to, this.filters.comparedPeriod);
      }

      if (isCurrentPeriod) this.reports.daily.loading = true;
      let dailyReport = await client.get('v3/reports/sales/daily', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.daily = c)),
      });
      dailyReport = this.formatReport(dailyReport, 'day');

      if (isCurrentPeriod) {
        this.reports.daily = {
          current_period: dailyReport,
          previous_period: undefined,
        };
        reportsStore.selectAllBranchesInFirstInit();
        this.reports.daily.loading = false;
      } else {
        this.reports.daily.previous_period = dailyReport;
      }
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports.daily.loading = false;
      logger.error({ error, place: 'SalesPeriodStore fetchDailyReport' });
    }
  };

  @action fetchMonthlyReport = async (isCurrentPeriod: boolean) => {
    try {
      const params: IMonthlyReportParams = {
        ...reportsStore.getReportBuildParams(),
        from: moment(this.filters.startYearDate * 1000).format(dateFormatForRequest),
        to: moment(this.filters.endYearDate * 1000).format(dateFormatForRequest),
        months: this.filters.months.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.months.map((option) => option.value as number),
      };

      if (!isCurrentPeriod) {
        [params.from, params.to] = subtractDatePeriod(params.from, params.to, this.filters.comparedPeriod);
      }

      if (isCurrentPeriod) this.reports.monthly.loading = true;
      let monthlyReport = await client.get('v3/reports/sales/monthly', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.monthly = c)),
      });
      monthlyReport = this.formatReport(monthlyReport, 'month');

      if (isCurrentPeriod) {
        this.reports.monthly = {
          current_period: monthlyReport,
          previous_period: undefined,
        };
        reportsStore.selectAllBranchesInFirstInit();
        this.reports.monthly.loading = false;
      } else {
        this.reports.monthly.previous_period = monthlyReport;
      }
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports.monthly.loading = false;
      logger.error({ error, place: 'SalesPeriodStore fetchMonthlyReport' });
    }
  };

  @action exportPeriodReportToXls = async (
    params: IHourlyReportParams | IDailyReportParams | IMonthlyReportParams,
    reportKey: keyof IPeriodReports,
    fileName: string
  ) => {
    try {
      this.reports[reportKey].loading = true;
      const periodReport = await client.get(`v3/reports/sales/${reportKey}`, {
        params: {
          ...params,
          export_xls: 1,
          export_xls_url: window.IS_MOBILE ? 1 : 0,
        },
        cancelToken: getCancelToken((c) => (this.cancelReportBuild[reportKey] = c)),
        responseType: window.IS_MOBILE ? 'text' : 'blob',
      });
      this.reports[reportKey].loading = false;

      if (window.IS_MOBILE) {
        wms.downloadFile(periodReport);
      } else {
        downloadFile(periodReport, `${fileName}, ${moment().format('DD-MM-YYYY')}.xls`);
      }

      toastSuccess({ message: localeStore.t('toast_report_exported') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports[reportKey].loading = false;
      logger.error({ error, place: 'SalesPeriodStore exportPeriodReportToXls' });
    }
  };

  private formatReport = (report: { [key: string]: IResponseReportBody } | undefined, type: 'hour' | 'day' | 'month'): IReportRow[] | undefined => {
    if (type === 'hour') {
      return report
        ? Object.entries(report).map(([name, body]) => ({
            name: name.replaceAll(TIME_FORMAT.AM, localeStore.t(TIME_FORMAT.AM)).replaceAll(TIME_FORMAT.PM, localeStore.t(TIME_FORMAT.PM)),
            body,
          }))
        : undefined;
    } else if (type === 'day') {
      return report ? Object.entries(report).map(([name, body]) => ({ name: localeStore.t(`txt_${name.toLowerCase()}`), body })) : undefined;
    } else {
      return report ? Object.entries(report).map(([name, body]) => ({ name: localeStore.t(name), body })) : undefined;
    }
  };
}

export const salesPeriodStore = new SalesPeriodStore();
